import React, { useState } from "react";
import { InputComponent } from "../../core/Forms";
import * as UI from "../../common";
import View from "../../../assets/icons/svgicons/View.svg";
import {
  validateCreatePasswordForm,
  validateNewPasswordForm,
} from "../../../modules/settings/settings";
import { Button } from "../../common";
import ErrorComponent from "../../Error";
import { PasswordError } from "../../../@types/password";
import { useTranslation } from "react-i18next";

interface Props {
  onSubmit: (currentPassword: string, newPassword: string) => void;
  validationErrors: PasswordError;
  setValidationErrors: (error: PasswordError) => void;
  isLoading: boolean;
  onCancel: () => void;
  formData: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
  setFormData: React.Dispatch<
    React.SetStateAction<{
      currentPassword: string;
      newPassword: string;
      confirmPassword: string;
    }>
  >;
}

export const ChangePasswordForm: React.FC<Props> = ({
  onSubmit,
  validationErrors,
  setValidationErrors,
  isLoading,
  onCancel,
  formData,
  setFormData,
}) => {
  const { t } = useTranslation("settings");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  const handleNewPasswordSubmit = (
    e?: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    e?.preventDefault();
    const validationErrors = validateNewPasswordForm(
      formData?.newPassword,
      formData?.confirmPassword,
      formData?.currentPassword,
    );

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    try {
      onSubmit(formData?.currentPassword, formData.newPassword);
      setFormData({
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
      });
      setErrors({});
    } catch (error) {
      return <ErrorComponent errorCode={500} />;
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
    setValidationErrors({ ...validationErrors, currentPassword: "" });
  };

  return (
    <div className="bg-[#FFFFFF] rounded-[8px] w-full lg:w-full h-screen lg:h-[788px] text-[#212121]">
      <h1 className="text-[16px] md:text-[20px] font-semibold">
        {t("Settings.changepassword")}
      </h1>
      <p className="text-[14px] text-[#475467] font-medium md:text-[16px] mt-2 md:mt-3">
        {t("Settings.secureyouraccountwithastrongpassword")}
      </p>

      <hr className="mt-[10px] mb-[20px] md:my-6 w-full h-[1px] bg-[#000000] bg-opacity-[25%]" />

      <div className="w-full md:max-w-[834px]">
        <div className="md:flex justify-between items-center w-full mb-5">
          <div className="flex-1 text-[14px] md:text-[16px] lg:text-[18px] font-semibold mb-1 md:mb-2">
            {t("Settings.currentpassword")}
          </div>

          <div className="flex-1 flex flex-col justify-end">
            <InputComponent
              placeholder="Current Password"
              name="currentPassword"
              type="password"
              value={formData.currentPassword}
              handleChange={handleInputChange}
              className="w-full !pl-4 rounded h-10 md:h-12 border !border-[#A4A4A4]"
              image={View}
              error={errors.currentPassword}
              imageClassName="ml-[90%] mt-[-2em] md:mt-[-2em]"
            />
            {validationErrors.currentPassword && (
              <div className="text-[#FB0303] text-[13px]">
                {validationErrors.currentPassword}
              </div>
            )}
          </div>
        </div>
        {/* NEW PASSWORD */}
        <div className="md:flex justify-between items-center w-full mb-5">
          <div className="flex-1 text-[14px] md:text-[16px] lg:text-[18px] font-semibold mb-1 md:mb-2">
            New Password
          </div>

          <div className="flex-1 flex flex-col justify-end">
            <InputComponent
              placeholder="New Password"
              name="newPassword"
              type="password"
              value={formData.newPassword}
              handleChange={handleInputChange}
              className="w-full !pl-4 rounded h-10 md:h-12 border !border-[#A4A4A4]"
              error={errors.newPassword}
              imageClassName="ml-[90%] mt-[-2em] md:mt-[-2em]"
            />
          </div>
        </div>
        {/* CONFIRM PASSWORD */}
        <div className="md:flex justify-between items-center w-full mb-5">
          <div className="flex-1 text-[14px] md:text-[16px] lg:text-[18px] font-semibold mb-1 md:mb-2">
            Confirm new password
          </div>

          <div className="flex-1 flex flex-col justify-end">
            <InputComponent
              placeholder="Confirm new password"
              name="confirmPassword"
              type="password"
              image={View}
              value={formData.confirmPassword}
              handleChange={handleInputChange}
              className="w-full !pl-4 rounded h-10 md:h-12 border !border-[#A4A4A4]"
              error={errors.confirmPassword}
              imageClassName="ml-[90%] mt-[-2em] md:mt-[-2em]"
            />
          </div>
        </div>

        <div className="ml-auto flex justify-end mt-8 md:w-1/3 space-x-5">
          <Button
            size="full"
            text="Cancel"
            variant="secondary"
            font="semibold"
            onClick={() => {
              setFormData({
                currentPassword: "",
                newPassword: "",
                confirmPassword: "",
              });
            }}
            outline
          />

          <Button
            type="submit"
            text="Save Changes"
            isLoading={isLoading}
            disabled={isLoading}
            variant="secondary"
            font="semibold"
            onClick={(e) => handleNewPasswordSubmit(e)}
          />
        </div>
      </div>
    </div>
  );
};

export default ChangePasswordForm;
